import { useEffect, useState } from "react";
import { getOfflineImageURL } from "@/lib/offlineStore";
import { API } from "@/lib/api";
import { Package } from "lucide-react";

/**
 * <OfflineImg producto_id="..." size={200} />
 *
 * Renderiza la imagen de un producto priorizando el blob local de IndexedDB
 * cuando el navegador está offline. Si estamos online, va directo al endpoint
 * público. Si no hay imagen (ni local ni remota), muestra un placeholder gris.
 *
 * `size` = 200 | 400 (miniatura y tamaño para preview grande).
 */
export default function OfflineImg({ producto_id, size = 200, alt, className = "", onClick, testid }) {
  const [src, setSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    (async () => {
      if (!producto_id) { setSrc(null); return; }
      // Offline: intenta blob local primero
      if (!navigator.onLine) {
        const local = await getOfflineImageURL(producto_id, size);
        if (!alive) return;
        if (local) {
          objectUrl = local;
          setSrc(local);
          return;
        }
        setErrored(true);
        return;
      }
      // Online: URL remota; si falla la carga, prueba blob local como fallback
      setSrc(`${API}/catalogo/img/${producto_id}?w=${size}`);
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [producto_id, size]);

  const handleError = async () => {
    // Si falló la URL online, intentamos el blob local como fallback
    const local = await getOfflineImageURL(producto_id, size);
    if (local) {
      setSrc(local);
      setErrored(false);
    } else {
      setErrored(true);
    }
  };

  if (!src || errored) {
    return (
      <div
        className={`bg-muted flex items-center justify-center text-muted-foreground/50 ${className}`}
        title="Sin foto"
        onClick={onClick}
        data-testid={testid}
      >
        <Package className="w-5 h-5" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt || "producto"}
      className={className}
      loading="lazy"
      onLoad={() => setLoaded(true)}
      onError={handleError}
      onClick={onClick}
      data-testid={testid}
      style={loaded ? undefined : { backgroundColor: "hsl(var(--muted))" }}
    />
  );
}
